import { z } from "zod";
import { nullableUuid, optionalUuid } from "../lib/zod-helpers";
import { paginationSchema, sortSchema } from "./pagination";

/**
 * HCA Moravia — validační schémata pro `hca.aktivity` tRPC router.
 * Modul Aktivity: historie jednání CRM po vzoru Raynet (schůzky, telefonáty,
 * úkoly, e-maily, poznámky) s follow-up řetězem a připomínkami.
 */

export const aktivitaTypEnum = z.enum(["SCHUZKA", "TELEFONAT", "UKOL", "EMAIL", "POZNAMKA"]);
export type AktivitaTyp = z.infer<typeof aktivitaTypEnum>;

export const aktivitaStavEnum = z.enum(["PLANOVANA", "DOKONCENA", "ZRUSENA"]);
export type AktivitaStav = z.infer<typeof aktivitaStavEnum>;

const uuidMsg = "Neplatné UUID";

/** Připomínka N minut předem — 1 minuta až 1 týden (10 080 min). */
const pripominkaMinutSchema = z.coerce
  .number()
  .int("Připomínka musí být celé číslo minut")
  .min(1, "Připomínka musí být alespoň 1 minuta")
  .max(10_080, "Připomínka může být nejvýše týden předem");

const nazevSchema = z
  .string()
  .min(1, "Název je povinný")
  .max(255, "Název musí být 255 znaků nebo méně");

const popisSchema = z.string().max(10_000, "Popis musí být 10 000 znaků nebo méně");
const vysledekSchema = z.string().max(10_000, "Výsledek musí být 10 000 znaků nebo méně");

/**
 * Vytvoření aktivity. `vlastnikId` se v routeru defaultuje na ctx.user.id.
 * Aspoň jedna vazba (klient / firma / obchodní případ) je povinná — aktivita
 * bez vazby by se nikde v historii nezobrazila.
 *
 * `dokoncena: true` = rychlý záznam proběhlého jednání („Zaznamenat") —
 * vznikne rovnou DOKONCENA s `dokoncenoAt = planovanoOd` a případným
 * výsledkem. Typ POZNAMKA vzniká DOKONCENA vždy (řeší router).
 */
export const createAktivitaSchema = z
  .object({
    typ: aktivitaTypEnum,
    nazev: nazevSchema,
    popis: popisSchema.optional(),
    planovanoOd: z.coerce.date(),
    planovanoDo: z.coerce.date().optional(),
    pripominkaMinut: pripominkaMinutSchema.optional(),
    klientId: optionalUuid(uuidMsg),
    firmaId: optionalUuid(uuidMsg),
    obchodniPripadId: optionalUuid(uuidMsg),
    /** Delegace při vytvoření — default aktuální uživatel (router). */
    vlastnikId: z.string().max(255, "ID musí být 255 znaků nebo méně").optional(),
    /** Rychlý záznam už proběhlého jednání — vznikne rovnou DOKONCENA. */
    dokoncena: z.boolean().optional(),
    vysledek: vysledekSchema.optional(),
  })
  .refine((d) => d.klientId || d.firmaId || d.obchodniPripadId, {
    message: "Aktivita musí mít vazbu na klienta, firmu nebo obchodní případ.",
    path: ["klientId"],
  })
  .refine((d) => !d.planovanoDo || d.planovanoDo.getTime() >= d.planovanoOd.getTime(), {
    message: "Konec musí být po začátku.",
    path: ["planovanoDo"],
  });

/**
 * Úprava aktivity — jen dokud je PLANOVANA (hlídá router). `null` u vazeb
 * a volitelných polí = vymazat hodnotu, `undefined` = beze změny.
 */
export const updateAktivitaSchema = z
  .object({
    id: z.string().uuid(uuidMsg),
    typ: aktivitaTypEnum.optional(),
    nazev: nazevSchema.optional(),
    popis: popisSchema.nullable().optional(),
    planovanoOd: z.coerce.date().optional(),
    planovanoDo: z.coerce.date().nullable().optional(),
    pripominkaMinut: pripominkaMinutSchema.nullable().optional(),
    klientId: nullableUuid(uuidMsg),
    firmaId: nullableUuid(uuidMsg),
    obchodniPripadId: nullableUuid(uuidMsg),
    /** Delegace — předání aktivity jinému vlastníkovi. */
    vlastnikId: z.string().max(255, "ID musí být 255 znaků nebo méně").optional(),
  })
  .refine(
    (d) => !d.planovanoDo || !d.planovanoOd || d.planovanoDo.getTime() >= d.planovanoOd.getTime(),
    { message: "Konec musí být po začátku.", path: ["planovanoDo"] },
  );

/**
 * Dokončení aktivity — Raynet vzor „dokončit a naplánovat další krok".
 * `followUp` volitelně založí navazující aktivitu (zdědí vazby + vlastníka)
 * v jedné transakci.
 */
export const completeAktivitaSchema = z.object({
  id: z.string().uuid(uuidMsg),
  vysledek: vysledekSchema.optional(),
  followUp: z
    .object({
      typ: aktivitaTypEnum,
      nazev: nazevSchema,
      planovanoOd: z.coerce.date(),
      pripominkaMinut: pripominkaMinutSchema.optional(),
    })
    .optional(),
});

/** Zrušení aktivity — důvod se ukládá do `vysledek`. */
export const cancelAktivitaSchema = z.object({
  id: z.string().uuid(uuidMsg),
  duvod: z.string().max(10_000, "Důvod musí být 10 000 znaků nebo méně").optional(),
});

/** ID-only schema (remove). */
export const aktivitaIdSchema = z.object({
  id: z.string().uuid(uuidMsg),
});

/** Whitelist řazení aktivit — router mapuje klíče na sloupce. */
export const AKTIVITY_SORT_KEYS = ["planovano", "typ", "klient", "vlastnik", "stav"] as const;
export type AktivitySortKey = (typeof AKTIVITY_SORT_KEYS)[number];

/** Seznam aktivit s filtry — `od`/`do` filtrují `planovanoOd`. */
export const listAktivitSchema = paginationSchema
  .merge(sortSchema(AKTIVITY_SORT_KEYS, "desc"))
  .extend({
    klientId: optionalUuid(uuidMsg),
    firmaId: optionalUuid(uuidMsg),
    obchodniPripadId: optionalUuid(uuidMsg),
    vlastnikId: z.string().max(255, "ID musí být 255 znaků nebo méně").optional(),
    stav: aktivitaStavEnum.optional(),
    typ: aktivitaTypEnum.optional(),
    od: z.coerce.date().optional(),
    do: z.coerce.date().optional(),
  });

/**
 * Sloučená časová osa pro kartu klienta / firmy — právě jedna z vazeb.
 * U firmy timeline agreguje i aktivity a e-maily kontaktů té firmy.
 */
export const aktivityTimelineSchema = z
  .object({
    klientId: optionalUuid(uuidMsg),
    firmaId: optionalUuid(uuidMsg),
  })
  .refine((d) => Boolean(d.klientId) !== Boolean(d.firmaId), {
    message: "Zadejte právě jednu vazbu: klientId nebo firmaId.",
    path: ["klientId"],
  });

export type CreateAktivitaInput = z.infer<typeof createAktivitaSchema>;
export type UpdateAktivitaInput = z.infer<typeof updateAktivitaSchema>;
export type CompleteAktivitaInput = z.infer<typeof completeAktivitaSchema>;
export type CancelAktivitaInput = z.infer<typeof cancelAktivitaSchema>;
export type AktivitaIdInput = z.infer<typeof aktivitaIdSchema>;
export type ListAktivitInput = z.infer<typeof listAktivitSchema>;
export type AktivityTimelineInput = z.infer<typeof aktivityTimelineSchema>;
